import { rem } from "@certn/cascadia";
import {
  BasicButton,
  ButtonGroup,
  DateSelectField,
  Grid,
  GridItem,
  LegacyModal,
  Link,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalTrigger,
  PrimaryButton,
  TextInputField,
} from "@certn/cascadia-react";
import { useDetectMobileKeyboardOpen } from "@certn/utils";
import { css } from "@emotion/css";
import { Field, Form, Formik } from "formik";
import { Trans, useTranslation } from "react-i18next";
import { date, object, string } from "yup";

import { OffenseSelect } from "./OffenseSelect";
import { Conviction } from "./type";

type ConvictionsModalProps = {
  index: number;
  conviction?: Conviction;
  onSave: (conviction: Conviction) => void;
  trigger: JSX.Element;
  onDelete?: () => void;
};
export function ConvictionModal({
  conviction,
  onSave,
  index,
  trigger,
  onDelete,
}: ConvictionsModalProps) {
  const { t } = useTranslation();
  const isMobileKeyboardOpen = useDetectMobileKeyboardOpen();

  const schema = object().shape({
    date_of_sentence: date()
      .max(new Date(), t("Invalid date"))
      .required()
      .label(t("Date of sentence")),
    court_location: string().trim().required().label(t("Court location")),
    offense_description: string().required().label(t("Conviction offense")),
  });

  return (
    <ModalTrigger trigger={() => trigger}>
      {(close) => (
        <Formik
          initialValues={
            conviction
              ? {
                  ...conviction,
                  offense_description: `${conviction.offense} - ${conviction.description}`,
                }
              : {
                  court_location: "",
                  date_of_sentence: "",
                  offense: "",
                  offense_description: "",
                  index,
                }
          }
          onSubmit={(conviction) => {
            onSave(conviction);
            close();
          }}
          validationSchema={schema}
        >
          {({ values, setFieldValue }) => (
            <Form
              className={
                isMobileKeyboardOpen
                  ? css({
                      position: "fixed",
                      top: rem("space-20"),
                      left: rem("space-0"),
                    })
                  : css({})
              }
            >
              <LegacyModal width="456px">
                <ModalHeader
                  close={close}
                  title={
                    conviction ? t("Edit conviction") : t("Add conviction")
                  }
                />
                <ModalContent>
                  <Grid>
                    <GridItem>
                      <OffenseSelect
                        name="offense_description"
                        label={t("Conviction offense")}
                        onChange={({ code, description }) => {
                          setFieldValue("offense", code);
                          setFieldValue("description", description);
                        }}
                        testID="personal-history-modal-conviction-offense"
                      />
                    </GridItem>
                    <GridItem>
                      <Field
                        name="court_location"
                        label={t("Court location")}
                        as={TextInputField}
                        isRequired
                        testID="personal-history-modal-court-location-input"
                      />
                    </GridItem>
                    <GridItem>
                      <Field
                        label={t("Date of sentence")}
                        name="date_of_sentence"
                        isRequired
                        value={values.date_of_sentence}
                        as={DateSelectField}
                        testID="personal-history-modal-date-of-sentence-input"
                      />
                    </GridItem>
                  </Grid>
                </ModalContent>
                <ModalFooter
                  className={css({
                    justifyContent: onDelete ? "space-between" : "flex-end",
                  })}
                >
                  {onDelete && (
                    <Link
                      textStyle="global/body-secondary"
                      textColor="text-weak"
                      onPress={() => {
                        onDelete();
                        close();
                      }}
                      testID={`personal-history-conviction-${
                        index + 1
                      }-delete-conviction-button`}
                    >
                      <Trans>Delete conviction</Trans>
                    </Link>
                  )}
                  <ButtonGroup>
                    <BasicButton
                      size="medium"
                      onPress={close}
                      testID={`personal-history-conviction-${
                        index + 1
                      }-cancel-button`}
                    >
                      <Trans>Cancel</Trans>
                    </BasicButton>
                    <PrimaryButton
                      type="submit"
                      size="medium"
                      testID={`personal-history-conviction-${
                        index + 1
                      }-save-conviction-button`}
                    >
                      <Trans>Save conviction</Trans>
                    </PrimaryButton>
                  </ButtonGroup>
                </ModalFooter>
              </LegacyModal>
            </Form>
          )}
        </Formik>
      )}
    </ModalTrigger>
  );
}
